import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import RequestsService, { TPI_REQUEST_STATUS } from './Requests.service';
import './Requests.css';

const ALL_VALUE = 'ALL';
const WRAP_CELL_SX = { wordBreak: 'break-word', whiteSpace: 'normal' };

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusChip({ status }) {
  const isOpen = (status || '').toUpperCase() === TPI_REQUEST_STATUS.OPEN;
  return (
    <Chip
      size="small"
      label={status || '—'}
      sx={{
        fontWeight: 700,
        fontSize: '0.75rem',
        letterSpacing: 0.3,
        bgcolor: isOpen ? 'warning.light' : 'success.light',
        color: isOpen ? 'warning.dark' : 'success.dark',
        border: '1px solid',
        borderColor: isOpen ? 'warning.main' : 'success.main',
      }}
    />
  );
}

function Requests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusTab, setStatusTab] = useState(ALL_VALUE);

  // Close confirmation state
  const [requestToClose, setRequestToClose] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [closeError, setCloseError] = useState('');

  // Toast
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await RequestsService.getAllTpiRequests();
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading TPI requests:', err);
      setError(err.message || 'Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const counts = useMemo(() => {
    let open = 0;
    let closed = 0;
    for (const r of requests) {
      if ((r.requestStatus || '').toUpperCase() === TPI_REQUEST_STATUS.OPEN) open += 1;
      else if ((r.requestStatus || '').toUpperCase() === TPI_REQUEST_STATUS.CLOSED) closed += 1;
    }
    return { all: requests.length, open, closed };
  }, [requests]);

  const filteredRequests = useMemo(() => {
    let list = requests;
    if (statusTab !== ALL_VALUE) {
      list = list.filter((r) => (r.requestStatus || '').toUpperCase() === statusTab);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        [r.companyName, r.siteAddress, r.customerName, r.customerMobileNo, r.machineNames, r.requestTpiId]
          .some((field) => String(field ?? '').toLowerCase().includes(q))
      );
    }
    return list;
  }, [requests, statusTab, searchQuery]);

  const handleOpenCloseConfirm = (request) => {
    setRequestToClose(request);
    setCloseError('');
  };

  const handleCancelClose = () => {
    if (isClosing) return;
    setRequestToClose(null);
    setCloseError('');
  };

  const handleConfirmClose = async () => {
    if (!requestToClose) return;
    setIsClosing(true);
    setCloseError('');
    try {
      const result = await RequestsService.closeTpiRequest(requestToClose.requestTpiId);
      setRequests((prev) =>
        prev.map((r) =>
          r.requestTpiId === requestToClose.requestTpiId
            ? { ...r, requestStatus: TPI_REQUEST_STATUS.CLOSED }
            : r
        )
      );
      setRequestToClose(null);
      setToast({
        open: true,
        message: result?.message || 'Request closed successfully.',
        severity: 'success',
      });
    } catch (err) {
      console.error('Error closing TPI request:', err);
      setCloseError(err.message || 'Failed to close the request. Please try again.');
    } finally {
      setIsClosing(false);
    }
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  return (
    <div className="requests-page">
      <Box className="requests-page__header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <RequestQuoteIcon sx={{ color: 'primary.main', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
            TPI Requests
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadRequests} disabled={loading} size="small">
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Paper
        elevation={0}
        className="requests-page__toolbar"
        sx={{
          p: 1.75,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={statusTab}
          onChange={(_, v) => setStatusTab(v)}
          sx={{
            minHeight: 0,
            '& .MuiTabs-flexContainer': {
              gap: 0.5,
              backgroundColor: 'grey.100',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 0.5,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: 36,
              py: 0,
              px: 2,
              borderRadius: 1.5,
              color: 'text.secondary',
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'primary.main',
              backgroundColor: 'background.paper',
              boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab value={ALL_VALUE} label={`All (${counts.all})`} />
          <Tab value={TPI_REQUEST_STATUS.OPEN} label={`Open (${counts.open})`} />
          <Tab value={TPI_REQUEST_STATUS.CLOSED} label={`Closed (${counts.closed})`} />
        </Tabs>

        <TextField
          size="small"
          placeholder="Search by company, site, customer, mobile, machine…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, ml: 'auto' }}>
          {filteredRequests.length} of {requests.length}
        </Typography>
      </Paper>

      {error && (
        <div className="error-message" role="alert" aria-live="polite">
          <span className="message-icon" aria-hidden="true">!</span>
          <span className="error-message-text">{error}</span>
          <button
            type="button"
            className="error-message-close"
            onClick={() => setError('')}
            aria-label="Close message"
            title="Close"
          >
            ×
          </button>
        </div>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={44} thickness={4} sx={{ color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, fontWeight: 500 }}>
            Loading requests…
          </Typography>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
        >
          {filteredRequests.length === 0 ? (
            <Box sx={{ py: 10, textAlign: 'center', px: 3 }}>
              <RequestQuoteIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                {requests.length === 0 ? 'No requests found.' : 'No requests match your search/filter.'}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="medium"
                aria-label="TPI Requests"
                sx={{
                  '& .MuiTableCell-root': { py: 1.6, px: 2 },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    {[
                      ['Request ID', '9%'],
                      ['Status', '8%'],
                      ['Company', '13%'],
                      ['Site Address', '15%'],
                      ['Customer', '12%'],
                      ['Mobile No', '10%'],
                      ['Machines', '16%'],
                      ['Created At', '10%'],
                      ['Action', '7%'],
                    ].map(([h, w]) => (
                      <TableCell
                        key={h}
                        sx={{ width: w, fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRequests.map((req) => {
                    const isOpen = (req.requestStatus || '').toUpperCase() === TPI_REQUEST_STATUS.OPEN;
                    return (
                      <TableRow key={req.requestTpiId} hover>
                        <TableCell sx={{ fontWeight: 600, color: 'primary.main' }}>{req.requestTpiId}</TableCell>
                        <TableCell><StatusChip status={req.requestStatus} /></TableCell>
                        <TableCell sx={WRAP_CELL_SX}>{req.companyName || '—'}</TableCell>
                        <TableCell sx={WRAP_CELL_SX}>{req.siteAddress || '—'}</TableCell>
                        <TableCell sx={WRAP_CELL_SX}>{req.customerName || '—'}</TableCell>
                        <TableCell>{req.customerMobileNo || '—'}</TableCell>
                        <TableCell sx={WRAP_CELL_SX}>{req.machineNames || '—'}</TableCell>
                        <TableCell sx={{ fontSize: '0.8125rem' }}>{formatDate(req.createdAt)}</TableCell>
                        <TableCell>
                          {isOpen ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<CloseIcon />}
                              onClick={() => handleOpenCloseConfirm(req)}
                              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
                            >
                              Close
                            </Button>
                          ) : (
                            <Chip
                              size="small"
                              icon={<TaskAltIcon sx={{ fontSize: 16 }} />}
                              label="Closed"
                              sx={{ fontWeight: 600, bgcolor: 'grey.100', color: 'text.secondary' }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog
        open={!!requestToClose}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'error.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
          Close Request
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {closeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCloseError('')}>
              {closeError}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to close request{' '}
            <strong>#{requestToClose?.requestTpiId}</strong> raised by{' '}
            <strong>{requestToClose?.customerName}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Button onClick={handleCancelClose} color="inherit" disabled={isClosing} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClose}
            variant="contained"
            color="error"
            disabled={isClosing}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isClosing ? 'Closing…' : 'Close Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Requests;
